/**
 * Componente AutomationCard - Card de automação individual
 * Exibe informações de um fluxo de automação com ações rápidas
 * 
 * RELEASE: 1.0.0 - 2026-01-26 - Componente inicial para Kanban de Automações
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Play, Pause, ExternalLink, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { PhaseAutomation } from '@/hooks/usePhaseAutomations';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Props do componente
interface AutomationCardProps {
  /** Dados da automação */
  automation: PhaseAutomation;
  /** Callback ao clicar para editar */
  onEdit?: (flowId: string) => void;
  /** Classe CSS adicional */
  className?: string;
}

/**
 * Card compacto para exibição de uma automação
 * Mostra nome, status e ações rápidas
 */
export function AutomationCard({
  automation,
  onEdit,
  className,
}: AutomationCardProps) {
  const navigate = useNavigate();

  // Handler para abrir edição do fluxo
  const handleEdit = () => {
    if (onEdit) {
      onEdit(automation.id);
    } else {
      navigate(automation.edit_url);
    }
  };

  // Formata data de criação
  const createdAgo = formatDistanceToNow(new Date(automation.created_at), {
    addSuffix: true,
    locale: ptBR,
  });

  return (
    <div
      className={cn(
        'group p-2 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer',
        'flex flex-col gap-1.5 overflow-hidden w-full max-w-full',
        automation.status === 'inactive' && 'opacity-60',
        className
      )}
      onClick={handleEdit}
    >
      {/* Header: Ícone + Nome + Status */}
      <div className="flex items-start gap-1.5 flex-1 min-w-0">
        {/* Ícone de raio */}
        <div 
          className={cn(
            'p-1 rounded-md shrink-0',
            automation.status === 'active' 
              ? 'bg-amber-500/10 text-amber-500' 
              : 'bg-muted text-muted-foreground'
          )}
        >
          <Zap className="h-3 w-3" />
        </div>

        {/* Nome e tipo - Padrão DealCard: flex-1 min-w-0 sem wrapper extra */}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium truncate leading-tight w-0 min-w-full">
            {automation.name}
          </p>
          <p className="text-[10px] text-muted-foreground truncate">
            {automation.type === 'flow' ? 'Fluxo' : 'Regra'}
            {automation.category === 'action' && (
              <span className="ml-1 text-amber-600">• Move para cá</span>
            )}
          </p>
        </div>

        {/* Badge de status */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge 
                variant={automation.status === 'active' ? 'default' : 'secondary'}
                className={cn(
                  'h-5 px-1.5 text-[10px] shrink-0',
                  automation.status === 'active' && 'bg-green-500/10 text-green-600 border-green-200'
                )}
              >
                {automation.status === 'active' ? (
                  <Play className="h-3 w-3" />
                ) : (
                  <Pause className="h-3 w-3" />
                )}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top">
              {automation.status === 'active' ? 'Ativo' : 'Inativo'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Footer: Data de criação + Link externo */}
      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1 truncate">
          <Clock className="h-2.5 w-2.5 shrink-0" />
          {createdAgo}
        </span>
        
        <Button
          variant="ghost"
          size="icon"
          className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            handleEdit();
          }}
        >
          <ExternalLink className="h-2.5 w-2.5" />
        </Button>
      </div>
    </div>
  );
}
